import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from "react";
import {
    fetchServiceOrders,
    fetchBasciServiceOrders4Form,
    createServiceOrder,
    updateServiceOrder,
    deleteServiceOrder,
    //fetchServiceOrderResumen,
    getServiceOrderById,
    changeServiceOrderState,
    updateServiceManifest,
    fetchServiceOrdersPaginated,
    fetchServiceManifests,
    fetchHistorial,
    fetchClientServiceOrders,
} from '../../api/administration/serviceOrderApi';

export const useServiceOrders = () => {
    const queryClient = useQueryClient();

    const {
        data: serviceOrders = [],
        error,
        isLoading,
    } = useQuery({
        queryKey: ["ordenes-servicio"],
        queryFn: fetchServiceOrders,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
    });

    const {
        data: basicServiceOrders4Form = [],
        isLoading: loadingBasic4Form,
        error: errorBasic4Form,
    } = useQuery({
        queryKey: ["ordenes-servicio-basic4form"],
        queryFn: fetchBasciServiceOrders4Form,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: true,
    });

    /*const resumenQuery = useQuery({
        queryKey: ['ordenes-servicio-resumen'],
        queryFn: fetchServiceOrderResumen,
    });*/

    const createMutation = useMutation({
        mutationFn: createServiceOrder,
        onSuccess: () => {
            queryClient.invalidateQueries(['ordenes-servicio']);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateServiceOrder(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['ordenes-servicio']);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteServiceOrder,
        onSuccess: () => {
            queryClient.invalidateQueries(['ordenes-servicio']);
        },
    });

    const changeStateMutation = useMutation({
        mutationFn: ({ id, newState }) => changeServiceOrderState(id, newState),
        onSuccess: () => {
            queryClient.invalidateQueries(['ordenes-servicio']);
        },
    });

    const useFetchService = (id, options = {}) => {
        const { enabled = true } = options;

        return useQuery({
            queryKey: ["service", id],
            queryFn: () => getServiceOrderById(id),
            enabled: Boolean(id) && Boolean(enabled),
            staleTime: 0,
            retry: false,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            refetchOnMount: true,
        });
    };

    const updateManifestMutation = useMutation({
        mutationFn: ({ id, data }) => updateServiceManifest(id, data),
        onSuccess: (data, variables) => {
            // variables.data.OrdSerCabID contiene la orden afectada
            queryClient.invalidateQueries(['ordenes-servicio']);
            queryClient.invalidateQueries(["service", variables.data.OrdSerCabID]); // Si estás usando detalle
        },
    });

    const useFetchServiceManifests = (serviceOrderId) => {
        return useQuery({
            queryKey: ["service-manifests", serviceOrderId],
            queryFn: () => fetchServiceManifests(serviceOrderId),
            enabled: !!serviceOrderId,
            staleTime: 1000 * 60 * 5,
        });
    };

    return {
        serviceOrders,
        basicServiceOrders4Form, // 🔹 lista simple para el formulario
        loadingBasic4Form,
        errorBasic4Form,
        error,
        isLoading,
        //resumenQuery,
        createMutation,
        updateMutation,
        deleteMutation,
        changeStateMutation,
        useFetchService,
        updateManifestMutation,
        useFetchServiceManifests
    };
};

/*export const useClientServiceOrders = ({ page, limit, search = "", status = "TODOS", projectId = "TODOS" }) => {
    const {
        data = { data: [], total: 0, page: 1, limit: 10 },
        error,
        isLoading,
    } = useQuery({
        queryKey: ["ordenes-servicio-cliente", page, limit, search, status, projectId],
        queryFn: () => fetchClientServiceOrders({ page, limit, search, status, projectId }),
        keepPreviousData: true,
        retry: false,
        refetchOnWindowFocus: true,
    });

    return {
        serviceOrdersClient: data.data,
        total: data.total,
        page: data.page,
        limit: data.limit,
        error,
        isLoading,
    };
};*/

export const useClientServiceOrders = ({
    page = 1,
    limit = 25,
    search = "",
    status = "TODOS",
    situacion = "TODOS",
    projectId = "TODOS",
    fechaDesde = "",
    fechaHasta = "",
    enabled = true,
} = {}) => {
    // Normalizamos para tener un queryKey estable y evitar refetches innecesarios
    const normalizedParams = useMemo(
        () => ({
            page,
            limit,
            search: search?.trim() || "",
            status,
            situacion,
            projectId,
            fechaDesde,
            fechaHasta,
        }),
        [page, limit, search, status, situacion, projectId, fechaDesde, fechaHasta]
    );

    const {
        data = {
            data: [],
            total: 0,
            page: 1,
            limit: 10,
            totals: null, // ⬅️ sumatorias globales (opcional pero recomendado)
        },
        error,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: ["ordenes-servicio-cliente", normalizedParams],
        queryFn: () => fetchClientServiceOrders(normalizedParams),
        keepPreviousData: true,
        retry: false,
        refetchOnWindowFocus: true,
        enabled,
        staleTime: 1000 * 60 * 2, // 2 min: los montos no cambian tan seguido
    });

    return {
        serviceOrdersClient: data.data ?? [],
        total: data.total ?? 0,
        page: data.page ?? page,
        limit: data.limit ?? limit,
        totals: data.totals ?? null,   // 👈 NUEVO
        error,
        isLoading,
        isFetching,
    };
};

export const usePaginatedServiceOrders = ({ page, limit }) => {
    const queryClient = useQueryClient();

    const {
        data = { data: [], total: 0, page: 1, limit: 10 },
        error,
        isLoading,
    } = useQuery({
        queryKey: ["ordenes-servicio", page, limit],
        queryFn: () => fetchServiceOrdersPaginated({ page, limit }),
        keepPreviousData: true, // mantiene la pag anterior visible mientras carga la nueva
        staleTime: 1000 * 60 * 2,
    });

    return {
        serviceOrders: data.data,
        total: data.total,
        page: data.page,
        limit: data.limit,
        error,
        isLoading
    };
};

// Hook para obtener historial de un servicio
export const useHistorial = (serviceId) => {
    const { data: historial = [], error, isLoading } = useQuery({
        queryKey: ["historial", serviceId],
        queryFn: () => fetchHistorial(serviceId),
        enabled: !!serviceId,
    });

    return { historial, error, isLoading };
};

export const useFetchServiceOrderById = (id, options = {}) => {
    const { enabled = true } = options;

    return useQuery({
        queryKey: ["service-order", id],
        queryFn: () => getServiceOrderById(id),
        enabled: Boolean(id) && Boolean(enabled),
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
    });
};

export const useChangeServiceOrderState = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, newState }) => changeServiceOrderState(id, newState),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["service-order", variables.id] });
            queryClient.invalidateQueries({ queryKey: ["ordenes-servicio"] });
            queryClient.invalidateQueries({ queryKey: ["ordenes-servicio-cliente"] });
        },
    });
};